import { useState } from 'react';
import type { Money } from '@/api/generated/models';
import type {
  AgreementDetails,
  PaymentSchedule,
  ScheduledPayment,
} from '@/api/schemas';
import { PaymentStatusBadge } from '@/components/agreement/PaymentStatusBadge';
import { DocumentOpenButton } from '@/components/DocumentOpenButton';
import { ResultsPagination } from '@/components/search/ResultsPagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useClock } from '@/lib/clock';
import { formatMoney, NOT_AVAILABLE } from '@/lib/display';
import { DateText } from '@/lib/formatDate';
import { invoiceDocument } from '@/lib/invoiceDocument';
import { pageCount, pageSlice } from '@/lib/pagination';
import { paymentCommission, paymentState } from '@/lib/payments';

type Party = 'customer' | 'dealer';

const PARTIES: Record<
  Party,
  {
    label: string;
    tab: string;
    header: string;
    amount: (payment: ScheduledPayment) => Money;
  }
> = {
  customer: {
    label: 'Customer Payments',
    tab: 'data-[state=active]:border-customer-party-stroke data-[state=active]:bg-[var(--customer-surface)] data-[state=active]:text-[var(--customer-ink)]',
    header:
      'bg-[var(--customer-surface)] [&_tr]:border-y [&_tr]:border-customer-party-stroke',
    amount: (payment) => payment.amount,
  },
  dealer: {
    label: 'Dealer Commissions',
    tab: 'data-[state=active]:border-dealer-party-stroke data-[state=active]:bg-[var(--dealer-surface)] data-[state=active]:text-[var(--dealer-ink)]',
    header:
      'bg-[var(--dealer-surface)] [&_tr]:border-y [&_tr]:border-dealer-party-stroke',
    amount: paymentCommission,
  },
};

function isPaid(payment: ScheduledPayment): boolean {
  return payment.status === 'Paid';
}

function PaymentRows({
  agreement,
  schedule,
  party,
  payments,
}: {
  agreement: AgreementDetails;
  schedule: PaymentSchedule;
  party: Party;
  payments: readonly ScheduledPayment[];
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const clock = useClock();
  const today = clock.today();

  return (
    <>
      <div className="overflow-x-auto pb-2">
        <Table className="table-fixed">
          <TableHeader className={PARTIES[party].header}>
            <TableRow>
              <TableHead className="w-24">Paym. #</TableHead>
              <TableHead className="w-28">Amount</TableHead>
              <TableHead className="w-28">Due Date</TableHead>
              <TableHead className="w-28">Invoice #</TableHead>
              <TableHead className="w-28">Invoice Date</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-32">Received Paym. Date</TableHead>
              <TableHead className="w-36">VINs related</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageSlice(payments, page, pageSize).map((payment) => (
              <TableRow key={payment.number}>
                <TableCell>
                  <p>{payment.number}</p>
                  <p className="text-[12px] leading-4 text-[rgba(0,0,0,0.6)]">
                    Out of {schedule.payments.length}
                  </p>
                </TableCell>
                <TableCell>
                  {formatMoney(PARTIES[party].amount(payment))}
                </TableCell>
                <TableCell>
                  <DateText value={payment.dueDate} />
                </TableCell>
                <TableCell>
                  {payment.invoiceNumbers.length > 0
                    ? payment.invoiceNumbers.join(', ')
                    : NOT_AVAILABLE}
                </TableCell>
                <TableCell>
                  <DateText value={payment.invoiceDate} />
                </TableCell>
                <TableCell>
                  <PaymentStatusBadge state={paymentState(payment, today)} />
                </TableCell>
                <TableCell>
                  {payment.receivedDate ? (
                    <DateText value={payment.receivedDate} />
                  ) : (
                    NOT_AVAILABLE
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span>VIN List</span>
                    <DocumentOpenButton
                      documentName="invoice"
                      load={async () =>
                        (
                          await import('@/components/agreement/invoicePdf')
                        ).renderInvoicePdf(
                          invoiceDocument(agreement, schedule, payment, {
                            today,
                            timeZone: clock.timeZone(),
                            locale: navigator.language,
                          }),
                        )
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {payments.length > 0 ? (
        <ResultsPagination
          page={page}
          totalPages={pageCount(payments.length, pageSize)}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      ) : null}
    </>
  );
}

function PartySchedule({
  agreement,
  schedule,
  party,
}: {
  agreement: AgreementDetails;
  schedule: PaymentSchedule;
  party: Party;
}) {
  const comingUp = schedule.payments.filter((payment) => !isPaid(payment));
  const history = schedule.payments.filter(isPaid);
  const total = schedule.payments.length;

  return (
    <Tabs
      defaultValue={comingUp.length > 0 ? 'coming-up' : 'history'}
      className="px-4 pt-3"
    >
      <TabsList className="w-fit">
        <TabsTrigger
          value="history"
          className="px-4 data-[state=active]:border-tab-active-rule"
        >
          {`History (${history.length}/${total})`}
        </TabsTrigger>
        <TabsTrigger
          value="coming-up"
          className="px-4 data-[state=active]:border-tab-active-rule"
        >
          {`Coming Up (${comingUp.length}/${total})`}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="history" className="pt-4">
        <PaymentRows
          agreement={agreement}
          schedule={schedule}
          party={party}
          payments={history}
        />
      </TabsContent>
      <TabsContent value="coming-up" className="pt-4">
        <PaymentRows
          agreement={agreement}
          schedule={schedule}
          party={party}
          payments={comingUp}
        />
      </TabsContent>
    </Tabs>
  );
}

export function PaymentScheduleTable({
  agreement,
  schedule,
}: {
  agreement: AgreementDetails;
  schedule: PaymentSchedule;
}) {
  return (
    <Tabs defaultValue="customer">
      <TabsList className="gap-0 border-b-0">
        {(['customer', 'dealer'] as const).map((party) => (
          <TabsTrigger
            key={party}
            value={party}
            className={`rounded-t-[4px] px-4 pt-2 uppercase ${PARTIES[party].tab}`}
          >
            {PARTIES[party].label}
          </TabsTrigger>
        ))}
      </TabsList>
      {(['customer', 'dealer'] as const).map((party) => (
        <TabsContent
          key={party}
          value={party}
          className="rounded-[0_4px_4px_4px] border border-schedule-stroke"
        >
          <PartySchedule
            agreement={agreement}
            schedule={schedule}
            party={party}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}
